<template>
  <Teleport to="body">
    <v-dialog
      :model-value="modelValue"
      max-width="760"
      persistent
      class="ctx-studio-root"
      content-class="ctx-studio-root"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <SnackbarComp :SnackbarComponent="snackbar" />

      <div class="ctx-studio" role="dialog" aria-modal="true">
        <div class="ctx-studio__glow" aria-hidden="true" />

        <header class="ctx-studio__header">
          <div class="ctx-studio__brand">
            <div class="ctx-studio__mark" aria-hidden="true">
              <v-icon
                :icon="isCreate ? 'mdi-bookshelf' : 'mdi-file-document-edit-outline'"
                size="22"
              />
            </div>
            <div class="ctx-studio__titles">
              <p class="ctx-studio__eyebrow">
                {{ isCreate ? 'New knowledge product' : 'Edit knowledge product' }}
              </p>
              <h2 class="ctx-studio__title">
                {{ isCreate ? 'Shape your product' : 'Refine this product' }}
              </h2>
              <p class="ctx-studio__subtitle">
                {{
                  isCreate
                    ? 'Give it a name, then write or upload the source document.'
                    : 'Click a file to refine its text, or add more content below.'
                }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="ctx-studio__close"
            aria-label="Close"
            :disabled="saving"
            @click="close"
          >
            <v-icon icon="mdi-close" size="18" />
          </button>
        </header>

        <div class="ctx-studio__body">
          <v-form ref="formRef" v-model="isValid" class="ctx-studio__form">
            <section class="ctx-studio__name-card">
              <label class="ctx-studio__field">
                <span class="ctx-studio__label">Product name</span>
                <span class="ctx-studio__field-hint">
                  A short title so you can recognise this knowledge later.
                </span>
                <v-text-field
                  v-model="form.contextName"
                  placeholder="e.g. Product FAQ · Support playbook"
                  variant="solo"
                  flat
                  density="comfortable"
                  hide-details="auto"
                  :rules="nameRules"
                  class="ctx-studio__input"
                />
              </label>
            </section>

            <section v-if="!isCreate" class="ctx-studio__files-card">
              <div class="ctx-studio__section-head">
                <div>
                  <h3 class="ctx-studio__section-title">Attached files</h3>
                  <p class="ctx-studio__section-copy">
                    Click a .txt file to load and refine its content below.
                  </p>
                </div>
                <span class="ctx-studio__count-pill">
                  {{ existingFiles.length }}
                </span>
              </div>

              <ul v-if="existingFiles.length" class="ctx-studio__file-rail">
                <li
                  v-for="file in existingFiles"
                  :key="file.s3_key || file.id"
                  class="ctx-studio__file-chip"
                  :class="{
                    'ctx-studio__file-chip--active':
                      refiningFile?.s3_key === file.s3_key,
                  }"
                >
                  <button
                    type="button"
                    class="ctx-studio__file-chip-main"
                    :disabled="renamingKey === file.s3_key || loadingFileKey === file.s3_key"
                    @click="openFileForRefine(file)"
                  >
                    <span class="ctx-studio__file-icon" aria-hidden="true">
                      <v-icon
                        v-if="loadingFileKey === file.s3_key"
                        icon="mdi-loading"
                        size="18"
                        class="ctx-studio__spin"
                      />
                      <v-icon
                        v-else
                        :icon="
                          isPdfFilename(file.filename)
                            ? 'mdi-file-pdf-box'
                            : 'mdi-file-document-outline'
                        "
                        size="18"
                      />
                    </span>
                    <template v-if="renamingKey === file.s3_key">
                      <v-text-field
                        v-model="renameValue"
                        density="compact"
                        variant="solo"
                        flat
                        hide-details
                        class="ctx-studio__rename-input"
                        @click.stop
                        @keyup.enter="confirmRename(file)"
                      />
                    </template>
                    <div v-else class="ctx-studio__file-meta">
                      <span class="ctx-studio__file-name">{{ file.filename }}</span>
                      <span class="ctx-studio__file-status">
                        {{
                          refiningFile?.s3_key === file.s3_key
                            ? refiningDirty
                              ? 'Editing · unsaved changes'
                              : 'Open in writing pad'
                            : file.uploadedOnLabel
                        }}
                      </span>
                    </div>
                  </button>
                  <div class="ctx-studio__file-actions">
                    <template v-if="renamingKey === file.s3_key">
                      <button
                        type="button"
                        class="ctx-studio__mini-btn ctx-studio__mini-btn--primary"
                        :disabled="fileBusyKey === file.s3_key"
                        @click="confirmRename(file)"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        class="ctx-studio__mini-btn"
                        :disabled="fileBusyKey === file.s3_key"
                        @click="cancelRename"
                      >
                        Cancel
                      </button>
                    </template>
                    <template v-else>
                      <button
                        type="button"
                        class="ctx-studio__icon-btn"
                        aria-label="Rename file label"
                        title="Rename label"
                        :disabled="Boolean(fileBusyKey) || Boolean(loadingFileKey)"
                        @click="startRename(file)"
                      >
                        <v-icon icon="mdi-rename-outline" size="16" />
                      </button>
                      <button
                        type="button"
                        class="ctx-studio__icon-btn ctx-studio__icon-btn--danger"
                        aria-label="Remove file"
                        :disabled="
                          existingFiles.length <= 1 || Boolean(fileBusyKey)
                        "
                        @click="removeFile(file)"
                      >
                        <v-icon icon="mdi-trash-can-outline" size="16" />
                      </button>
                    </template>
                  </div>
                </li>
              </ul>
              <p v-else class="ctx-studio__empty-line">No files attached yet.</p>
            </section>

            <section class="ctx-studio__composer">
              <div class="ctx-studio__section-head">
                <div>
                  <h3 class="ctx-studio__section-title">
                    {{
                      isCreate
                        ? 'Source content'
                        : refiningFile
                          ? 'Refine file content'
                          : 'Add more content'
                    }}
                  </h3>
                  <p class="ctx-studio__section-copy">
                    {{ composerHint }}
                  </p>
                </div>

                <div
                  v-if="!refiningFile"
                  class="ctx-studio__mode"
                  role="tablist"
                  aria-label="Content mode"
                >
                  <button
                    type="button"
                    class="ctx-studio__mode-btn"
                    :class="{ 'ctx-studio__mode-btn--active': contentMode === 'manual' }"
                    role="tab"
                    :aria-selected="contentMode === 'manual'"
                    @click="contentMode = 'manual'"
                  >
                    <v-icon icon="mdi-fountain-pen-tip" size="15" />
                    Write
                  </button>
                  <button
                    type="button"
                    class="ctx-studio__mode-btn"
                    :class="{ 'ctx-studio__mode-btn--active': contentMode === 'upload' }"
                    role="tab"
                    :aria-selected="contentMode === 'upload'"
                    @click="clearRefineAndUpload"
                  >
                    <v-icon icon="mdi-tray-arrow-up" size="15" />
                    Upload
                  </button>
                </div>
                <button
                  v-else
                  type="button"
                  class="ctx-studio__mini-btn"
                  @click="clearRefine"
                >
                  Done refining
                </button>
              </div>

              <div v-if="contentMode === 'upload'" class="ctx-studio__drop">
                <v-file-input
                  v-model="pickedFiles"
                  multiple
                  accept=".txt,.pdf,application/pdf,text/plain"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  variant="solo"
                  flat
                  density="comfortable"
                  hide-details="auto"
                  placeholder="Drop .txt / .pdf or browse"
                  class="ctx-studio__file-input"
                  @update:model-value="onFilesPicked"
                />

                <ul v-if="documents.length" class="ctx-studio__doc-tabs">
                  <li v-for="doc in documents" :key="doc.id">
                    <button
                      type="button"
                      class="ctx-studio__doc-tab"
                      :class="{ 'ctx-studio__doc-tab--active': doc.id === activeDocId }"
                      @click="selectDocument(doc.id)"
                    >
                      <v-icon
                        :icon="
                          doc.kind === 'pdf'
                            ? 'mdi-file-pdf-box'
                            : 'mdi-file-document-outline'
                        "
                        size="14"
                      />
                      <span>{{ doc.filename }}</span>
                      <span
                        v-if="doc.kind === 'text' && doc.dirty"
                        class="ctx-studio__doc-tag"
                      >
                        Edited
                      </span>
                    </button>
                    <button
                      type="button"
                      class="ctx-studio__icon-btn"
                      aria-label="Remove selected file"
                      @click="removeDocument(doc.id)"
                    >
                      <v-icon icon="mdi-close" size="14" />
                    </button>
                  </li>
                </ul>
              </div>

              <div class="ctx-studio__surface">
                <div class="ctx-studio__surface-bar">
                  <span class="ctx-studio__surface-label">
                    {{ surfaceLabel }}
                  </span>
                  <span class="ctx-studio__surface-hint">
                    {{ surfaceHint }}
                  </span>
                </div>

                <template v-if="contentMode === 'manual' || refiningFile">
                  <div v-if="refiningFile && isPdfFilename(refiningFile.filename)" class="ctx-studio__pdf-state">
                    <div class="ctx-studio__pdf-orb">
                      <v-icon icon="mdi-file-pdf-box" size="28" />
                    </div>
                    <p class="ctx-studio__pdf-title">{{ refiningFile.filename }}</p>
                    <p class="ctx-studio__pdf-copy">
                      PDF text editing is not supported. Upload a replacement .txt file instead.
                    </p>
                  </div>
                  <v-textarea
                    v-else
                    v-model="manualText"
                    variant="solo"
                    flat
                    density="comfortable"
                    hide-details="auto"
                    rows="11"
                    no-resize
                    :disabled="Boolean(loadingFileKey)"
                    :placeholder="
                      refiningFile
                        ? 'Refine this document content…'
                        : 'Start writing the knowledge this product should carry…'
                    "
                    class="ctx-studio__editor"
                  />
                </template>

                <template v-else-if="activeDoc">
                  <v-textarea
                    v-if="activeDoc.kind === 'text'"
                    :model-value="activeDoc.text"
                    variant="solo"
                    flat
                    density="comfortable"
                    hide-details="auto"
                    rows="11"
                    no-resize
                    :disabled="activeDoc.loading"
                    placeholder="File text will appear here…"
                    class="ctx-studio__editor"
                    @update:model-value="onActiveTextEdit"
                  />
                  <div v-else class="ctx-studio__pdf-state">
                    <div class="ctx-studio__pdf-orb">
                      <v-icon icon="mdi-file-pdf-box" size="28" />
                    </div>
                    <p class="ctx-studio__pdf-title">{{ activeDoc.filename }}</p>
                    <p class="ctx-studio__pdf-copy">
                      This PDF will upload unchanged. Switch to a .txt file to edit text.
                    </p>
                  </div>
                </template>

                <div v-else class="ctx-studio__pdf-state">
                  <div class="ctx-studio__pdf-orb ctx-studio__pdf-orb--muted">
                    <v-icon icon="mdi-tray-arrow-up" size="28" />
                  </div>
                  <p class="ctx-studio__pdf-title">Waiting for a document</p>
                  <p class="ctx-studio__pdf-copy">
                    Upload a file above to preview and refine it here.
                  </p>
                </div>
              </div>
            </section>
          </v-form>
        </div>

        <footer class="ctx-studio__footer">
          <button
            type="button"
            class="ctx-studio__btn ctx-studio__btn--ghost"
            :disabled="saving"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="ctx-studio__btn ctx-studio__btn--primary"
            :disabled="!canSubmit || saving"
            @click="submit"
          >
            <v-progress-circular
              v-if="saving"
              indeterminate
              size="16"
              width="2"
              color="white"
            />
            <template v-else>
              <v-icon
                :icon="isCreate ? 'mdi-check-circle-outline' : 'mdi-content-save-outline'"
                size="18"
              />
              {{ isCreate ? 'Create product' : 'Save changes' }}
            </template>
          </button>
        </footer>
      </div>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { downloadContextS3Text } from '@/utils/contextS3'
import {
  KNOWLEDGE_BASE_SEQUENTIAL_STEM,
  nextKnowledgeBaseSequence,
} from '@/mixins/Organization/UploadKnowledgeBase'
import '@/styles/contexts/form-dialog.css'

defineOptions({ name: 'ContextFormDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  mode: {
    type: String,
    default: 'CREATE',
    validator: (value) => ['CREATE', 'EDIT'].includes(value),
  },
  context: { type: Object, default: null },
  saveFn: { type: Function, required: true },
  removeFileFn: { type: Function, default: null },
  renameFileFn: { type: Function, default: null },
  /** Filenames already used across all contexts, for sequential naming. */
  existingFilenames: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const formRef = ref(null)
const isValid = ref(false)
const saving = ref(false)
const snackbar = ref({})
// Call behaviour (greeting/mission prompt/allow_manual_leads) and Dashboard
// Digest settings live on the Product detail page's own Settings tab now
// (ContextDetailPage.vue) -- this dialog is down to create/rename/KB-content
// editing, the one piece that's genuinely a dedicated editing surface, not
// tab-shaped.
const form = ref({
  contextName: '',
})
const contentMode = ref('manual')
const manualText = ref('')
const pickedFiles = ref([])
const documents = ref([])
const activeDocId = ref('')
const existingFiles = ref([])
const renamingKey = ref('')
const renameValue = ref('')
const fileBusyKey = ref('')
const refiningFile = ref(null)
const refiningOriginalText = ref('')
const loadingFileKey = ref('')

const isCreate = computed(() => props.mode !== 'EDIT')

const nameRules = [
  (v) => Boolean(String(v ?? '').trim()) || 'Product name is required',
  (v) => String(v ?? '').trim().length <= 120 || 'Max 120 characters',
]

const activeDoc = computed(
  () => documents.value.find((doc) => doc.id === activeDocId.value) || null,
)

const refiningDirty = computed(() => {
  if (!refiningFile.value || isPdfFilename(refiningFile.value.filename)) {
    return false
  }
  return String(manualText.value ?? '') !== String(refiningOriginalText.value ?? '')
})

const composerHint = computed(() => {
  if (refiningFile.value) {
    if (isPdfFilename(refiningFile.value.filename)) {
      return 'This PDF cannot be edited as text. Upload a .txt replacement if needed.'
    }
    return refiningDirty.value
      ? 'Edit the text below, then Save changes to replace this file.'
      : 'Loaded from storage — edit freely, then save to update.'
  }
  if (contentMode.value === 'manual') {
    return 'Write freely — we convert it to a .txt on save.'
  }
  return 'Upload files, preview text, edit, then publish.'
})

const surfaceLabel = computed(() => {
  if (refiningFile.value) {
    return refiningFile.value.filename || 'Refine file'
  }
  if (contentMode.value === 'manual') return 'Writing pad'
  return activeDoc.value?.filename || 'Preview'
})

const surfaceHint = computed(() => {
  if (refiningFile.value) {
    if (isPdfFilename(refiningFile.value.filename)) return 'PDF · view only'
    return refiningDirty.value ? 'Unsaved edits' : 'Loaded for editing'
  }
  if (contentMode.value === 'manual') return 'Plain text · saved as .txt'
  if (activeDoc.value?.kind === 'pdf') return 'PDF uploads as-is'
  if (activeDoc.value) return 'Editable before upload'
  return 'Choose a file to preview'
})

const hasNewDocuments = computed(() => {
  if (refiningFile.value) {
    if (isPdfFilename(refiningFile.value.filename)) return false
    return refiningDirty.value && Boolean(String(manualText.value ?? '').trim())
  }
  if (contentMode.value === 'manual') {
    return Boolean(String(manualText.value ?? '').trim())
  }
  return (
    documents.value.length > 0 &&
    documents.value.every((doc) => {
      if (doc.kind === 'pdf') return Boolean(doc.file)
      return Boolean(String(doc.text ?? '').trim())
    })
  )
})

const canSubmit = computed(() => {
  const nameOk = Boolean(String(form.value.contextName ?? '').trim())
  if (!nameOk) return false
  if (!isCreate.value) return true
  return hasNewDocuments.value
})

function isPdfFilename(filename) {
  return String(filename ?? '')
    .toLowerCase()
    .endsWith('.pdf')
}

function syncExistingFiles(context) {
  existingFiles.value = Array.isArray(context?.kb_files)
    ? context.kb_files.map((file) => ({ ...file }))
    : []
  renamingKey.value = ''
  renameValue.value = ''
  fileBusyKey.value = ''
}

function detectKind(file) {
  const name = String(file?.name ?? '').toLowerCase()
  const type = String(file?.type ?? '').toLowerCase()
  if (type.includes('pdf') || name.endsWith('.pdf')) return 'pdf'
  return 'text'
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

function textToFile(filename, text) {
  const blob = new Blob([String(text ?? '')], { type: 'text/plain' })
  return new File([blob], filename, { type: 'text/plain' })
}

function clearRefine() {
  refiningFile.value = null
  refiningOriginalText.value = ''
  loadingFileKey.value = ''
  manualText.value = ''
  contentMode.value = 'manual'
}

function clearRefineAndUpload() {
  clearRefine()
  contentMode.value = 'upload'
}

async function openFileForRefine(file) {
  if (!file?.s3_key || renamingKey.value === file.s3_key) return

  if (refiningFile.value?.s3_key === file.s3_key && !loadingFileKey.value) {
    contentMode.value = 'manual'
    return
  }

  refiningFile.value = { ...file }
  contentMode.value = 'manual'
  pickedFiles.value = []
  documents.value = []
  activeDocId.value = ''

  if (isPdfFilename(file.filename)) {
    manualText.value = ''
    refiningOriginalText.value = ''
    return
  }

  loadingFileKey.value = file.s3_key
  manualText.value = ''
  refiningOriginalText.value = ''
  try {
    const text = await downloadContextS3Text(file.s3_key)
    if (refiningFile.value?.s3_key !== file.s3_key) return
    manualText.value = text
    refiningOriginalText.value = text
  } catch (error) {
    if (refiningFile.value?.s3_key === file.s3_key) {
      refiningFile.value = null
      manualText.value = ''
      refiningOriginalText.value = ''
    }
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(error, 'Could not load file content for editing.'),
    }
  } finally {
    if (loadingFileKey.value === file.s3_key) {
      loadingFileKey.value = ''
    }
  }
}

function firstEditableFile(files = existingFiles.value) {
  return files.find((file) => file?.s3_key && !isPdfFilename(file.filename)) || null
}

async function autoOpenFirstFile() {
  if (isCreate.value) return
  const first = firstEditableFile()
  if (first) await openFileForRefine(first)
}

function resetForm() {
  form.value = {
    contextName: props.context?.context_name || '',
  }
  contentMode.value = 'manual'
  manualText.value = ''
  pickedFiles.value = []
  documents.value = []
  activeDocId.value = ''
  refiningFile.value = null
  refiningOriginalText.value = ''
  loadingFileKey.value = ''
  syncExistingFiles(props.context)
}

function close() {
  emit('update:modelValue', false)
}

function startRename(file) {
  renamingKey.value = file.s3_key
  renameValue.value = file.filename || ''
}

function cancelRename() {
  renamingKey.value = ''
  renameValue.value = ''
}

async function confirmRename(file) {
  if (!props.renameFileFn) return
  const nextName = String(renameValue.value ?? '').trim()
  if (!nextName) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: 'Filename is required.',
    }
    return
  }

  fileBusyKey.value = file.s3_key
  try {
    const response = await props.renameFileFn({
      context_id: props.context?.context_id,
      s3_key: file.s3_key,
      filename: nextName,
    })
    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, 'Failed to rename file'),
      }
      return
    }
    file.filename = nextName
    if (refiningFile.value?.s3_key === file.s3_key) {
      refiningFile.value = { ...refiningFile.value, filename: nextName }
    }
    cancelRename()
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: 'File renamed.',
    }
    emit('saved')
  } finally {
    fileBusyKey.value = ''
  }
}

async function removeFile(file) {
  if (!props.removeFileFn) return
  if (existingFiles.value.length <= 1) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText:
        'Cannot remove the last file. Every product must keep at least one document.',
    }
    return
  }

  fileBusyKey.value = file.s3_key
  try {
    const response = await props.removeFileFn({
      context_id: props.context?.context_id,
      s3_key: file.s3_key,
      fileCount: existingFiles.value.length,
    })
    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, 'Failed to remove file'),
      }
      return
    }
    existingFiles.value = existingFiles.value.filter(
      (item) => item.s3_key !== file.s3_key,
    )
    if (refiningFile.value?.s3_key === file.s3_key) {
      clearRefine()
      const next = firstEditableFile()
      if (next) await openFileForRefine(next)
    }
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: 'File removed.',
    }
    emit('saved')
  } finally {
    fileBusyKey.value = ''
  }
}

function selectDocument(id) {
  activeDocId.value = id
}

function removeDocument(id) {
  documents.value = documents.value.filter((doc) => doc.id !== id)
  if (activeDocId.value === id) {
    activeDocId.value = documents.value[0]?.id || ''
  }
  pickedFiles.value = documents.value.map((doc) => doc.file).filter(Boolean)
}

function onActiveTextEdit(value) {
  const doc = activeDoc.value
  if (!doc || doc.kind !== 'text') return
  doc.text = value
  doc.dirty = true
}

async function onFilesPicked(value) {
  const list = Array.isArray(value) ? value : value ? [value] : []
  const nextDocs = []

  for (const file of list) {
    if (!file?.name) continue
    const kind = detectKind(file)
    const id = `${file.name}-${file.size}-${file.lastModified}`
    const existing = documents.value.find((doc) => doc.id === id)

    if (existing) {
      nextDocs.push(existing)
      continue
    }

    const doc = {
      id,
      filename: file.name,
      kind,
      file,
      text: '',
      dirty: false,
      loading: kind === 'text',
    }
    nextDocs.push(doc)

    if (kind === 'text') {
      try {
        doc.text = await readFileAsText(file)
      } catch {
        doc.text = ''
        snackbar.value = {
          SnackbarVmodel: true,
          SnackbarColor: 'error',
          SnackbarText: `Could not read "${file.name}".`,
        }
      } finally {
        doc.loading = false
      }
    }
  }

  documents.value = nextDocs
  if (!nextDocs.some((doc) => doc.id === activeDocId.value)) {
    activeDocId.value = nextDocs[0]?.id || ''
  }
}

function nextSequentialFilename(offset = 0) {
  const usedNames = [
    ...props.existingFilenames,
    ...existingFiles.value.map((file) => file.filename),
  ]
  const start = nextKnowledgeBaseSequence(usedNames)
  return (ext) => `${KNOWLEDGE_BASE_SEQUENTIAL_STEM}-${start + offset++}.${ext}`
}

function buildUploadFiles() {
  // Refine keeps the original filename so it replaces the same document.
  if (refiningFile.value && refiningDirty.value) {
    const baseName = String(refiningFile.value.filename || 'document')
      .replace(/\.[^.]+$/, '')
    const filename = `${baseName || 'document'}.txt`
    return [textToFile(filename, manualText.value)]
  }

  const nextName = nextSequentialFilename()

  if (contentMode.value === 'manual') {
    return [textToFile(nextName('txt'), manualText.value)]
  }

  return documents.value.map((doc) => {
    if (doc.kind === 'pdf') {
      return new File([doc.file], nextName('pdf'), {
        type: doc.file.type || 'application/pdf',
        lastModified: doc.file.lastModified,
      })
    }
    return textToFile(nextName('txt'), doc.text)
  })
}

async function submit() {
  const validation = await formRef.value?.validate?.()
  if (validation && validation.valid === false) return
  if (!canSubmit.value) return

  if (isCreate.value && !hasNewDocuments.value) return

  const isRefining = Boolean(refiningFile.value && refiningDirty.value)
  if (isRefining && !String(manualText.value ?? '').trim()) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: 'Refined content cannot be empty.',
    }
    return
  }

  saving.value = true
  try {
    const payload = {
      mode: props.mode,
      context_id: props.context?.context_id,
      context_name: form.value.contextName,
    }

    if (isCreate.value || hasNewDocuments.value) {
      payload.files = buildUploadFiles()
    }

    if (isRefining) {
      payload.replaceFile = {
        s3_key: refiningFile.value.s3_key,
        filename: refiningFile.value.filename,
      }
    }

    const response = await props.saveFn(payload)

    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(
          response,
          isCreate.value ? 'Failed to create product' : 'Failed to update product',
        ),
      }
      return
    }

    const successText = isCreate.value
      ? 'Product created and files uploaded.'
      : isRefining
        ? 'File content updated.'
        : hasNewDocuments.value
          ? 'Product updated and files uploaded.'
          : 'Product updated successfully.'

    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: successText,
    }
    emit('saved')
    close()
  } catch (error) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(error, 'Request failed'),
    }
  } finally {
    saving.value = false
  }
}

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return
    resetForm()
    await autoOpenFirstFile()
  },
)

watch(
  () => props.context,
  (next) => {
    if (props.modelValue && !isCreate.value) {
      syncExistingFiles(next)
      if (next?.context_name) {
        form.value.contextName = next.context_name
      }
    }
  },
)
</script>
